import {
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text,
  Link,
  useToast,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  Box,
  useColorModeValue
} from '@chakra-ui/react';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Signup() {
  const navigate = useNavigate();
  const toast = useToast();
  const { signUp, error: authError, loading, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.username) {
      return 'Please fill in all required fields';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (formData.username.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      return 'Username can only contain letters, numbers, and underscores';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      toast({
        title: "Validation Error",
        description: validationError,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    const result = await signUp(formData.email, formData.password, formData.username);
    if (result.success) {
      toast({
        title: "Account created",
        description: "You've successfully signed up!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate('/dashboard');
    } else {
      toast({
        title: "Signup Error",
        description: result.error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Replaced useColorModeValue with direct semantic tokens below
  // const bgColor = useColorModeValue('white', 'gray.800') // Replaced -> bg.default (applied to Container)
  // const textColor = useColorModeValue('gray.600', 'gray.200') // Replaced -> text.muted
  // const boxBg = useColorModeValue('white', 'gray.700') // Replaced -> bg.surface
  // const inputBg = useColorModeValue('white', 'gray.600') // Replaced -> bg.surface

  return (
    <Container maxW="container.md" py={10} bg="bg.default"> {/* Apply theme bg */}
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading size={{ base: 'lg', md: 'xl' }}>Create Account</Heading> {/* Responsive size */}
          <Text mt={2} color="text.muted" fontSize={{ base: 'sm', md: 'md' }}> {/* Use theme token + responsive font */}
            Join the community to document and research megalithic sites
          </Text>
        </Box>

        <Box
          p={8}
          bg="bg.surface" // Use theme token
          boxShadow="lg"
          borderRadius="xl"
          color="text.default" // Use default text color for form elements
        >
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Username</FormLabel>
                <Input
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Choose a username"
                  bg="bg.surface" // Use theme token
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  bg="bg.surface" // Use theme token
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    name="password"
                    type={show ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    bg="bg.surface" // Use theme token
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={() => setShow(!show)}>
                      {show ? "Hide" : "Show"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Confirm Password</FormLabel>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  bg="bg.surface" // Use theme token
                />
              </FormControl>

              {authError && (
                <FormControl isInvalid={true}>
                  <FormErrorMessage>{authError}</FormErrorMessage>
                </FormControl>
              )}

              <Button
                type="submit"
                colorScheme="brand.primary" // Use theme scheme
                size="lg"
                width="full"
                isLoading={loading}
                loadingText="Creating account..."
              >
                Create Account
              </Button>
            </VStack>
          </form>
        </Box>

        <Text textAlign="center" color="text.muted" fontSize={{ base: 'sm', md: 'md' }}> {/* Use theme token + responsive font */}
          Already have an account?{" "}
          <Link as={RouterLink} to="/login" color="text.link"> {/* Use theme link color */}
            Log in
          </Link>
        </Text>
      </VStack>
    </Container>
  );
}
